import { useEffect, useState } from 'react';
import { TimerIcon, X } from 'lucide-react';
import { cn, memoClassNames } from '@/utils';
import {
  ProgressBar as BaseProgressBar,
  ProgressBarProps,
} from '@/components/progress';
import { QuizPlayerProgressProps as QuizProgressProps } from './types';
import { useTranslation } from '@/hooks/use-translation';
import { Button } from '../button';

type QuizPlayerProgressProps = React.ComponentProps<'div'> &
  QuizProgressProps & {
    slots?: { ProgressBar?: React.ElementType<ProgressBarProps> };
  };

const classes = memoClassNames('QuizPlayerProgress', [
  'root',
  'title',
  'timer',
  'stepper',
]);

export function QuizPlayerProgress({
  current = 1,
  total = 0,
  timeLimit,
  isTimerActive,
  onTimeUp,
  onCancel,
  className,
  slots = {},
  title,
  ...others
}: QuizPlayerProgressProps) {
  const { ProgressBar = BaseProgressBar } = slots;
  const { t } = useTranslation();
  const progress = !total ? 0 : (current / total) * 100;
  const stepLabel = !total
    ? null
    : t('quizProgress', {
        args: { value: current, total },
        fallback: `Domanda {value} di {total}`,
      });
  return (
    <div
      {...others}
      className={cn(
        classes.root,
        'mm:w-full mm:text-muted-foreground',
        className
      )}
    >
      <div className={`${classes.title} mm:mb-2 mm:font-semibold`}>{title}</div>
      <div className="mm:flex mm:mb-2 mm:gap-2 mm:items-center">
        <span className={`${classes.stepper} mm:grow mm:text-sm`}>
          {stepLabel}
        </span>
        {timeLimit && (
          <QuizPlayerTimer
            className={classes.timer}
            timeLimit={timeLimit}
            isActive={isTimerActive ?? true}
            onTimeUp={onTimeUp}
          />
        )}
        <Button size="xs" variant="ghost" onClick={onCancel}>
          <X />
        </Button>
      </div>
      <ProgressBar value={progress} />
    </div>
  );
}

QuizPlayerProgress.displayName = 'QuizPlayerProgress';

type QuizPlayerTimerProps = React.ComponentProps<'div'> & {
  timeLimit: number;
  onTimeUp?: () => void;
  isActive?: boolean;
};

function QuizPlayerTimer({
  timeLimit,
  onTimeUp = () => {},
  isActive,
  className,
  ...others
}: QuizPlayerTimerProps) {
  const [timeLeft, setTimeLeft] = useState(timeLimit);

  useEffect(() => {
    setTimeLeft(timeLimit);
  }, [timeLimit]);

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, onTimeUp]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div
      {...others}
      className={cn(
        'mm:flex mm:items-center mm:gap-2 mm:text-muted-foreground mm:text-sm mm:md:text-md',
        { 'mm:text-yellow-500 mm:font-semibold': timeLeft <= 10 },
        { 'mm:text-destructive mm:font-semibold': timeLeft === 0 },
        className
      )}
    >
      <TimerIcon size={16} />
      <span>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
}

QuizPlayerTimer.displayName = 'QuizPlayerTimer';
